import {NotePart, NotePartType} from 'notebook/util/NoteUtilConstants';

const CANVAS_DEFINITION_ID_PATTERN = 'cnvsdef_[a-zA-Z0-9]{8,23}';
const CANVAS_VERSION_ID_PATTERN = 'cnvsver_[a-zA-Z0-9]{8,23}';
const CANVAS_DEFINITION_ID_REGEX = new RegExp(`^${CANVAS_DEFINITION_ID_PATTERN}$`);
const CANVAS_VERSION_ID_REGEX = new RegExp(`^${CANVAS_VERSION_ID_PATTERN}$`);
const CANVAS_TAG_REGEX = /<bnch_canvas>\s*(\{[\s\S]*?\})\s*<\/bnch_canvas>/g;

export const CANVAS_ENTRY_PLACEHOLDER_TEXT = 'Canvas could not be inserted into this entry.';
export const CANVAS_ENTRY_PLACEHOLDER_HIGHLIGHT_COLOR = '#FBE98B';

export interface CanvasRef {
  canvasDefinitionId: string;
  canvasVersionId: string;
}

/**
 * Validates and parses a bnch_canvas tag's JSON content.
 * Returns the parsed data if valid, null if invalid.
 */
export function parseBnchCanvasJSON(
  content: string
): {canvasDefinitionId: string; canvasVersionId: string} | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch (error) {
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }

  if (typeof parsed !== 'object' || parsed == null || Array.isArray(parsed)) {
    return null;
  }

  const parsedObj = parsed as Record<string, unknown>;
  const {canvasDefinitionId, canvasVersionId} = parsedObj;
  if (
    typeof canvasDefinitionId !== 'string' ||
    !CANVAS_DEFINITION_ID_REGEX.test(canvasDefinitionId) ||
    typeof canvasVersionId !== 'string' ||
    !CANVAS_VERSION_ID_REGEX.test(canvasVersionId)
  ) {
    return null;
  }

  return {canvasDefinitionId, canvasVersionId};
}

export function canvasRefKey(canvas: CanvasRef): string {
  return `${canvas.canvasDefinitionId}:${canvas.canvasVersionId}`;
}

export function containsBnchCanvas(markdown: string): boolean {
  return Array.from(markdown.matchAll(CANVAS_TAG_REGEX), (match) => match[1]).some(
    (content) => parseBnchCanvasJSON(content) != null
  );
}

export function replaceBnchCanvasTagsWithPlaceholder(markdown: string): string {
  return markdown.replace(CANVAS_TAG_REGEX, (_match, json: string) => {
    if (parseBnchCanvasJSON(json) == null) {
      return '';
    }
    return `\n\n*${CANVAS_ENTRY_PLACEHOLDER_TEXT}*\n\n`;
  });
}

export function highlightCanvasPlaceholderNoteParts(noteParts: Array<NotePart>): Array<NotePart> {
  return noteParts.map((notePart) => {
    const formats = notePart.formats;
    if (
      notePart.type !== NotePartType.TEXT ||
      notePart.text !== CANVAS_ENTRY_PLACEHOLDER_TEXT ||
      formats == null
    ) {
      return notePart;
    }
    return {
      ...notePart,
      formats: {
        ...formats,
        backgroundColor: [
          {
            start: 0,
            end: notePart.text.length,
            data: {color: CANVAS_ENTRY_PLACEHOLDER_HIGHLIGHT_COLOR},
          },
        ],
      },
    };
  });
}
